import { useEffect, useState } from 'react';
import {
    TonConnectButton,
    useTonAddress,
    useTonConnectUI,
} from '@tonconnect/ui-react';
import { useTelegramWebApp } from './hooks/useTelegramWebApp';
import { useStore } from './store/useStore';
import api from './api';
import { sendTonTransaction } from './utils/tonTransfer';
import {
    Wallet,
    Shield,
    Users,
    Copy,
    BookOpen,
    Zap,
    Lock,
    Clock,
    AlertCircle,
    Download,
    MessageCircle,
    QrCode,
    X,
} from 'lucide-react';

const TON_LOGO = '/ton-logo.svg';

// ВРЕМЕННО: версия для принудительного обновления кеша (тех. работы)
const FORCE_RELOAD_VERSION = '20260220';
// КОНЕЦ ВРЕМЕННОГО БЛОКА

type Tab = 'main' | 'referral' | 'instructions';
type KeyTab = 'vless' | 'amnezia' | 'hysteria2' | 'trojan';

function App() {
    const {
        showAlert,
        showConfirm,
        hapticFeedback,
        user: tgUser,
        WebApp,
    } = useTelegramWebApp();
    const [tonConnectUI] = useTonConnectUI();
    const userFriendlyAddress = useTonAddress(); // Получаем user-friendly адрес

    // ВРЕМЕННО: принудительное обновление при первом открытии новой версии
    useEffect(() => {
        const key = 'app_version';
        if (localStorage.getItem(key) !== FORCE_RELOAD_VERSION) {
            localStorage.setItem(key, FORCE_RELOAD_VERSION);
            window.location.reload();
        }
    }, []);
    // КОНЕЦ ВРЕМЕННОГО БЛОКА

    // Логируем адрес при изменении
    useEffect(() => {
        if (userFriendlyAddress) {
            console.log('[App] Wallet address changed:', userFriendlyAddress);
        }
    }, [userFriendlyAddress]);

    const [activeTab, setActiveTab] = useState<Tab>('main');
    const [keyTab, setKeyTab] = useState<KeyTab>('vless');
    const [sendingTx, setSendingTx] = useState(false);
    const [trialEligible, setTrialEligible] = useState(false);
    const [activatingTrial, setActivatingTrial] = useState(false);
    const [showKeysChangedWarning, setShowKeysChangedWarning] = useState(false);
    const [keysChangedTimestamp, setKeysChangedTimestamp] = useState<number | null>(null);
    const [showVlessQr, setShowVlessQr] = useState(false);
    const [showAmneziaQr, setShowAmneziaQr] = useState<number | null>(null); // номер конфигурации (1-5)
    const [showHysteria2Qr, setShowHysteria2Qr] = useState(false);
    const [showTrojanQr, setShowTrojanQr] = useState(false);
    const [walletMismatch, setWalletMismatch] = useState(false);
    const [expectedWallet, setExpectedWallet] = useState('');

    const {
        user,
        balance,
        setBalance,
        setUser,
        activeSubscription,
        setActiveSubscription,
        plans,
        setPlans,
    } = useStore();

    const isConnected = !!userFriendlyAddress;

    // Проверить при загрузке подписки, нужно ли показать предупреждение
    useEffect(() => {
        if (activeSubscription?.id && activeSubscription.status === 'ACTIVE' && activeSubscription.planId !== 'trial') {
            const warningKey = `keys_changed_${activeSubscription.id}`;
            const savedTimestamp = localStorage.getItem(warningKey);

            if (savedTimestamp) {
                const timestamp = parseInt(savedTimestamp);
                const now = Date.now();
                const elapsed = now - timestamp;

                // Показать предупреждение, если прошло меньше 60 секунд
                if (elapsed < 60000) {
                    console.log('[Warning] Restoring keys changed warning from localStorage');
                    setKeysChangedTimestamp(timestamp);
                } else {
                    // Удалить устаревшую запись
                    localStorage.removeItem(warningKey);
                }
            }
        }
    }, [activeSubscription?.id]);

    // Таймер для скрытия блока предупреждения через 60 секунд
    useEffect(() => {
        if (keysChangedTimestamp) {
            const now = Date.now();
            const elapsed = now - keysChangedTimestamp;
            const remaining = 60000 - elapsed;

            if (remaining > 0) {
                setShowKeysChangedWarning(true);
                const timer = setTimeout(() => {
                    setShowKeysChangedWarning(false);
                    setKeysChangedTimestamp(null);
                    // Удалить запись из localStorage при скрытии
                    if (activeSubscription?.id) {
                        const warningKey = `keys_changed_${activeSubscription.id}`;
                        localStorage.removeItem(warningKey);
                    }
                }, remaining);
                return () => clearTimeout(timer);
            } else {
                setShowKeysChangedWarning(false);
                setKeysChangedTimestamp(null);
            }
        }
    }, [keysChangedTimestamp, activeSubscription?.id]);

    useEffect(() => {
        // Показываем UI сразу, не ждём isReady
        // Сохранить start_param в localStorage при первом открытии
        const startParam = WebApp?.initDataUnsafe?.start_param;
        if (startParam) {
            localStorage.setItem('referrer_tg_id', startParam);
            console.log('Saved referrer tgId to localStorage:', startParam);
        }

        if (isConnected && userFriendlyAddress) {
            // Проверить, было ли это первое подключение кошелька
            const wasDisconnected =
                localStorage.getItem('wallet_was_disconnected') === 'true';
            if (wasDisconnected) {
                console.log(
                    '[FirstConnection] Detected first wallet connection'
                );
                localStorage.removeItem('wallet_was_disconnected');
            }

            loadUserData();
        } else {
            // Load mock plans even without wallet connection
            loadMockPlans();
            // Отметить что кошелёк отключен (для определения первого подключения)
            if (!isConnected) {
                localStorage.setItem('wallet_was_disconnected', 'true');
            }
        }
    }, [isConnected, userFriendlyAddress]);

    // Poll subscription status when PENDING or when amneziaClients is missing
    useEffect(() => {
        const hasAmneziaClients = activeSubscription?.amneziaClients &&
            Array.isArray(activeSubscription.amneziaClients) &&
            activeSubscription.amneziaClients.length === 5;

        console.log('[Polling] useEffect triggered', {
            userFriendlyAddress,
            activeSubscription,
            status: activeSubscription?.status,
            hasAmneziaClients,
        });

        const shouldPoll =
            userFriendlyAddress &&
            activeSubscription &&
            (activeSubscription.status === 'PENDING' ||
                !hasAmneziaClients);

        if (shouldPoll) {
            console.log('[Polling] Starting polling for subscription status', {
                status: activeSubscription.status,
                hasAmneziaClients,
                subscriptionId: activeSubscription.id,
            });

            const interval = setInterval(async () => {
                try {
                    console.log('[Polling] Checking subscription status...');
                    // Use noCache: true to prevent browser caching
                    const result = await api.subscriptions.getActive(
                        userFriendlyAddress,
                        true
                    );
                    console.log('[Polling] Result:', result);

                    if (result.success && result.data) {
                        // Update subscription state
                        const wasPending = activeSubscription?.status === 'PENDING';
                        const newHasClients = result.data.amneziaClients &&
                            Array.isArray(result.data.amneziaClients) &&
                            result.data.amneziaClients.length === 5;

                        console.log('[Polling] Updating subscription state:', {
                            status: result.data.status,
                            hasClients: newHasClients,
                            clientsLength: result.data.amneziaClients?.length,
                            amneziaClients: result.data.amneziaClients,
                        });

                        // Force new object reference to trigger re-render
                        setActiveSubscription({ ...result.data });

                        // If status is ACTIVE and all 5 clients are ready, stop polling
                        if (result.data.status === 'ACTIVE' && newHasClients) {
                            console.log(
                                '[Polling] Subscription fully ready! Stopping poll.'
                            );
                            clearInterval(interval);
                            hapticFeedback('success');

                            // Show alert only if status changed from PENDING
                            if (wasPending) {
                                showAlert('Подписка активирована! Ключи доступа готовы.');
                            }

                            // Проверить localStorage на флаг Trial→Paid
                            if (result.data.id) {
                                const warningKey = `keys_changed_${result.data.id}`;
                                const savedTimestamp = localStorage.getItem(warningKey);
                                if (savedTimestamp) {
                                    const timestamp = parseInt(savedTimestamp);
                                    const now = Date.now();
                                    const elapsed = now - timestamp;
                                    if (elapsed < 60000) {
                                        console.log('[Polling] Found trial-to-paid flag, showing warning');
                                        setKeysChangedTimestamp(timestamp);
                                    }
                                }
                            }
                        } else {
                            console.log(
                                '[Polling] Still waiting... Status:',
                                result.data.status,
                                'Has clients:',
                                newHasClients,
                                'Clients array:',
                                result.data.amneziaClients
                            );
                        }
                    } else {
                        console.error(
                            '[Polling] Failed:',
                            result.error || 'Unknown error'
                        );
                    }
                } catch (error) {
                    console.error('[Polling] Exception:', error);
                }
            }, 3000); // Poll every 3 seconds

            return () => {
                console.log('[Polling] Stopping polling');
                clearInterval(interval);
            };
        } else {
            console.log('[Polling] Not starting - conditions not met', {
                hasWallet: !!userFriendlyAddress,
                hasSubscription: !!activeSubscription,
                status: activeSubscription?.status,
                hasAmneziaClients,
            });
        }
    }, [
        userFriendlyAddress,
        activeSubscription?.status,
        activeSubscription?.amneziaClients,
        activeSubscription?.id,
    ]);

    const loadMockPlans = () => {
        const mockPlans = [
            {
                id: '1_month',
                name: '1 Месяц',
                price: 2,
                duration: 30,
                bandwidth: '∞',
            },
            {
                id: '3_months',
                name: '3 Месяца',
                price: 5,
                duration: 90,
                bandwidth: '∞',
            },
            {
                id: '6_months',
                name: '6 Месяцев',
                price: 9,
                duration: 180,
                bandwidth: '∞',
            },
        ];
        setPlans(mockPlans as any);

        // Сбросить состояние пользователя и подписки при отключении кошелька
        setUser(null);
        setBalance(0);
        setActiveSubscription(null);
        setTrialEligible(false); // Сбросить право на пробную подписку при отключении
    };

    const loadUserData = async () => {
        if (!userFriendlyAddress) return;

        try {
            // Не блокируем UI глобальным loading - данные грузятся асинхронно
            console.log('[LoadUserData] Starting to load user data...');

            const mockPlans = [
                {
                    id: '1_month',
                    name: '1 Месяц',
                    price: 2,
                    duration: 30,
                    bandwidth: '∞',
                },
                {
                    id: '3_months',
                    name: '3 Месяца',
                    price: 5,
                    duration: 90,
                    bandwidth: '∞',
                },
                {
                    id: '6_months',
                    name: '6 Месяцев',
                    price: 9,
                    duration: 180,
                    bandwidth: '∞',
                },
            ];

            try {
                // Извлечь Telegram ID пользователя из разных источников
                const telegramUser = tgUser || WebApp.initDataUnsafe?.user;
                const tgId = telegramUser?.id
                    ? String(telegramUser.id)
                    : undefined;
                const username = telegramUser?.username;
                const firstName = telegramUser?.first_name;

                console.log('Telegram User Data:', {
                    tgUser,
                    telegramUser,
                    tgId,
                    username,
                    firstName,
                    initDataUnsafe: WebApp.initDataUnsafe,
                });

                // Отправляем wallet address, tgId, username и firstName в API
                // ВАЖНО: getMe должен завершиться ПЕРВЫМ, так как он создает пользователя
                console.log('[LoadUserData] Step 1: Calling getMe to create/update user...');
                let userData;
                try {
                    userData = await api.user.getMe(
                        userFriendlyAddress,
                        tgId,
                        username,
                        firstName
                    );
                    console.log('[LoadUserData] Step 1 completed. User exists:', userData.success);
                } catch (error) {
                    console.error('[LoadUserData] getMe failed:', error);
                    userData = { success: false as const, error: error instanceof Error ? error.message : 'Unknown error' };
                }

                // Проверка несовпадения кошелька (один tgId — один кошелёк)
                if (userData.success && userData.data?.walletMismatch) {
                    console.warn('[LoadUserData] Wallet mismatch detected. Expected:', userData.data.expectedWallet);
                    setWalletMismatch(true);
                    setExpectedWallet(userData.data.expectedWallet || '');
                    return;
                }
                setWalletMismatch(false);

                // Теперь запускаем остальные запросы параллельно
                console.log('[LoadUserData] Step 2: Loading plans and subscription...');
                const results = await Promise.allSettled([
                    api.subscriptions.getPlans(),
                    api.subscriptions.getActive(userFriendlyAddress, true), // noCache: true for fresh data
                ]);

                // Извлекаем результаты
                const plansData = results[0].status === 'fulfilled' ? results[0].value : { success: false };
                const subscriptionData = results[1].status === 'fulfilled' ? results[1].value : { success: false };

                // Логируем ошибки для каждого failed запроса
                if (results[0].status === 'rejected') {
                    console.error('[LoadUserData] getPlans failed:', results[0].reason);
                }
                if (results[1].status === 'rejected') {
                    console.error('[LoadUserData] getActive failed:', results[1].reason);
                }

                console.log('[LoadUserData] API Results:', {
                    userData: userData.success,
                    plansData: plansData.success,
                    subscriptionData: subscriptionData.success,
                });

                if (userData.success && userData.data) {
                    setUser(userData.data);
                    setBalance(userData.data.balance);

                    // Устанавливаем trial eligibility из данных пользователя
                    setTrialEligible(userData.data.trialEligible);
                    console.log('[LoadUserData] Trial eligibility from user data:', userData.data.trialEligible);

                    // Проверить, есть ли сохраненный referrer (из start_param или localStorage)
                    const startParamFromUrl =
                        WebApp.initDataUnsafe?.start_param;
                    const startParamFromStorage =
                        localStorage.getItem('referrer_tg_id');
                    const startParam =
                        startParamFromUrl || startParamFromStorage;

                    console.log('Referral check:', {
                        startParamFromUrl,
                        startParamFromStorage,
                        startParam,
                        userReferrerId: userData.data.referrerId,
                        shouldLinkReferrer: !!(
                            startParam && !userData.data.referrerId
                        ),
                    });

                    if (startParam && !userData.data.referrerId) {
                        // Это реферальная ссылка, и у пользователя еще нет реферера
                        try {
                            console.log('Attempting to link referrer:', {
                                wallet: userFriendlyAddress,
                                referrerTgId: startParam,
                            });
                            const result = await api.user.linkReferrer(
                                userFriendlyAddress,
                                startParam
                            );
                            console.log('Referrer link result:', result);

                            // Если успешно привязали, очищаем localStorage
                            if (result.success) {
                                localStorage.removeItem('referrer_tg_id');
                                console.log(
                                    'Referrer linked successfully, localStorage cleared'
                                );
                            } else {
                                console.warn(
                                    'Referrer linking failed:',
                                    result
                                );
                            }
                        } catch (err) {
                            console.error('Failed to link referrer:', err);
                        }
                    } else if (!startParam) {
                        console.log('No referrer to link');
                    } else if (userData.data.referrerId) {
                        console.log(
                            'User already has a referrer:',
                            userData.data.referrerId
                        );
                    }
                }

                if (plansData.success && plansData.data) {
                    setPlans(plansData.data);
                } else {
                    setPlans(mockPlans as any);
                }

                if (subscriptionData.success && subscriptionData.data) {
                    console.log(
                        '[LoadUserData] Setting subscription:',
                        subscriptionData.data
                    );
                    setActiveSubscription(subscriptionData.data);
                } else {
                    console.log(
                        '[LoadUserData] No subscription data:',
                        subscriptionData
                    );
                }
            } catch (apiError) {
                console.log(
                    'API не доступен, используем моковые данные для UI preview'
                );
                setPlans(mockPlans as any);
                setTrialEligible(false);
            }
        } catch (error) {
            console.error('Failed to load user data:', error);
            setTrialEligible(false);
        }
    };

    const handleActivateTrial = async () => {
        if (!userFriendlyAddress) {
            showAlert('Подключите кошелек');
            return;
        }

        try {
            setActivatingTrial(true);
            hapticFeedback('medium');
            console.log('[ActivateTrial] Activating trial subscription...');

            const result = await api.user.activateTrial(userFriendlyAddress);

            if (result.success && result.data) {
                console.log('[ActivateTrial] Trial activated:', result.data);
                setActiveSubscription(result.data);
                setTrialEligible(false);
                hapticFeedback('success');

                // Если подписка создалась со статусом PENDING, показываем сообщение о создании
                if (result.data.status === 'PENDING') {
                    showAlert('⏳ Подписка создается. Ключи будут готовы в течение 1 минуты.');
                } else {
                    showAlert('Пробная подписка активирована на 3 дня!');
                }
            } else {
                console.error('[ActivateTrial] Failed:', result);
                hapticFeedback('error');
                showAlert(result.error || 'Не удалось активировать пробную подписку');
            }
        } catch (error: any) {
            console.error('[ActivateTrial] Error:', error);
            hapticFeedback('error');
            showAlert(error.message || 'Ошибка активации пробной подписки');
        } finally {
            setActivatingTrial(false);
        }
    };

    const handleDeposit = async (amount?: number) => {
        if (!isConnected || !userFriendlyAddress) {
            showAlert('Подключите кошелек');
            return;
        }

        try {
            setSendingTx(true);
            hapticFeedback('medium');

            // Определяем сумму: либо переданная, либо минимальный тариф
            const depositAmount = amount || Math.min(...plans.map((p) => p.price));

            // Отправляем TON транзакцию
            await sendTonTransaction({
                tonConnectUI,
                amount: depositAmount,
            });

            hapticFeedback('success');
            showAlert(
                `Транзакция отправлена! Баланс обновится автоматически.`
            );

            // Запускаем polling баланса каждые 5 секунд до 2 минут
            let attempts = 0;
            const maxAttempts = 24; // 2 минуты (24 * 5 секунд)
            const currentBalance = balance;

            const pollBalance = setInterval(async () => {
                attempts++;

                try {
                    const response = await fetch(
                        `/api/user/me?wallet=${userFriendlyAddress}`
                    );
                    const data = await response.json();

                    if (data.success && data.data) {
                        const newBalance = Number(data.data.balance);

                        // Если баланс изменился - обновляем и останавливаем polling
                        if (newBalance !== currentBalance) {
                            setBalance(newBalance);
                            clearInterval(pollBalance);
                            console.log('[Deposit] Balance updated:', newBalance);
                        }
                    }
                } catch (error) {
                    console.error('[Deposit] Error polling balance:', error);
                }

                // Останавливаем после maxAttempts
                if (attempts >= maxAttempts) {
                    clearInterval(pollBalance);
                    console.log('[Deposit] Polling stopped after max attempts');
                }
            }, 5000);
        } catch (error: any) {
            hapticFeedback('error');
            showAlert(
                `Ошибка: ${error.message || 'Не удалось отправить транзакцию'}`
            );
        } finally {
            setSendingTx(false);
        }
    };

    const handleBuyPlan = async (planId: string) => {
        if (!isConnected) {
            showAlert('Подключите кошелек для покупки');
            return;
        }

        const plan = plans.find((p) => p.id === planId);
        if (!plan) return;

        if (balance < plan.price) {
            hapticFeedback('error');
            const confirmed = await showConfirm(
                'Недостаточно средств. Пополнить баланс?'
            );
            if (confirmed) {
                await handleDeposit(plan.price);
            }
            return;
        }

        const confirmed = await showConfirm(
            `Купить подписку "${plan.name}" за ${plan.price} TON?`
        );

        if (!confirmed) return;

        // Проверить, была ли trial подписка до покупки
        const wasTrialSubscription = activeSubscription?.planId === 'trial';
        console.log('[Purchase] wasTrialSubscription:', wasTrialSubscription, 'activeSubscription:', activeSubscription);

        try {
            hapticFeedback('medium');

            const result = await api.subscriptions.purchase(
                userFriendlyAddress,
                planId
            );

            if (result.success && result.data) {
                hapticFeedback('success');

                // Set subscription from purchase response (should be PENDING)
                console.log('[Purchase] Subscription created:', result.data);

                // ВАЖНО: Сохранить флаг Trial→Paid СРАЗУ, до setActiveSubscription
                if (wasTrialSubscription && result.data.id) {
                    console.log('[Purchase] Saving trial-to-paid flag for subscription', result.data.id);
                    const warningKey = `keys_changed_${result.data.id}`;
                    localStorage.setItem(warningKey, Date.now().toString());
                }

                setActiveSubscription(result.data);

                // Обновить баланс сразу после покупки
                try {
                    const userData = await api.user.getMe(userFriendlyAddress);
                    if (userData.success && userData.data) {
                        console.log('[Purchase] Balance updated:', userData.data.balance);
                        setUser(userData.data);
                        setBalance(userData.data.balance);
                    }
                } catch (err) {
                    console.error('[Purchase] Failed to reload user data:', err);
                }

                // Show different message based on status
                if (result.data.status === 'PENDING') {
                    showAlert(
                        '✅ Подписка создается! Генерируются ключи доступа...'
                    );

                    // Начать polling для обновления статуса подписки
                    pollSubscriptionStatus(userFriendlyAddress);
                } else {
                    showAlert('✅ Подписка успешно активирована!');

                    // Показать блок предупреждения (флаг уже в localStorage)
                    if (result.data.id) {
                        const warningKey = `keys_changed_${result.data.id}`;
                        const savedTimestamp = localStorage.getItem(warningKey);
                        if (savedTimestamp) {
                            setKeysChangedTimestamp(parseInt(savedTimestamp));
                        }
                    }
                }
            } else {
                hapticFeedback('error');
                showAlert(`Ошибка покупки подписки: ${result.error || 'неизвестная ошибка'}`);
            }
        } catch (error) {
            console.error('[Purchase] Error:', error);
            hapticFeedback('error');

            // Даже если запрос упал по таймауту, проверим статус подписки
            showAlert('⏳ Проверяем статус подписки...');

            setTimeout(async () => {
                try {
                    const result = await api.subscriptions.getActive(userFriendlyAddress, true);
                    if (result.success && result.data) {
                        console.log('[Purchase] Found subscription after timeout:', result.data);
                        setActiveSubscription(result.data);

                        // Обновить баланс после timeout
                        const userData = await api.user.getMe(userFriendlyAddress);
                        if (userData.success && userData.data) {
                            console.log('[Purchase] Balance updated after timeout:', userData.data.balance);
                            setUser(userData.data);
                            setBalance(userData.data.balance);
                        }

                        if (result.data.status === 'PENDING') {
                            showAlert('✅ Подписка создается! Генерируются ключи доступа...');
                            pollSubscriptionStatus(userFriendlyAddress);
                        } else if (result.data.status === 'ACTIVE') {
                            showAlert('✅ Подписка активирована!');

                            // Показать блок предупреждения (флаг уже в localStorage)
                            if (result.data.id) {
                                const warningKey = `keys_changed_${result.data.id}`;
                                const savedTimestamp = localStorage.getItem(warningKey);
                                if (savedTimestamp) {
                                    setKeysChangedTimestamp(parseInt(savedTimestamp));
                                }
                            }
                        }
                    } else {
                        showAlert('❌ Ошибка покупки подписки');
                    }
                } catch (err) {
                    console.error('[Purchase] Failed to check subscription status:', err);
                    showAlert('❌ Ошибка покупки подписки');
                }
            }, 2000);
        }
    };

    // Polling для обновления статуса подписки (PENDING → ACTIVE)
    const pollSubscriptionStatus = async (walletAddress: string) => {
        console.log('[Polling] Starting subscription status polling...');
        let attempts = 0;
        const maxAttempts = 60; // 60 попыток × 2 сек = 2 минуты макс

        const poll = async () => {
            try {
                attempts++;
                console.log(`[Polling] Attempt ${attempts}/${maxAttempts}`);

                const result = await api.subscriptions.getActive(walletAddress, true);

                if (result.success && result.data) {
                    console.log('[Polling] Subscription status:', result.data.status);

                    if (result.data.status === 'ACTIVE') {
                        // Подписка активирована!
                        console.log('[Polling] Subscription is now ACTIVE!');
                        setActiveSubscription({ ...result.data });

                        // Продолжаем polling если singboxClients ещё не готовы
                        if (!result.data.singboxClients) {
                            console.log('[Polling] Waiting for singbox clients...');
                            if (attempts < maxAttempts) {
                                setTimeout(poll, 3000);
                            }
                            return;
                        }

                        // Обновить баланс пользователя
                        try {
                            const userData = await api.user.getMe(walletAddress);
                            if (userData.success && userData.data) {
                                setUser(userData.data);
                                setBalance(userData.data.balance);
                                console.log('[Polling] Balance updated:', userData.data.balance);
                            }
                        } catch (err) {
                            console.error('[Polling] Failed to update balance:', err);
                        }

                        showAlert('✅ Подписка активирована! Ключи доступа готовы.');

                        // Показать блок предупреждения (флаг уже в localStorage)
                        if (result.data.id) {
                            const warningKey = `keys_changed_${result.data.id}`;
                            const savedTimestamp = localStorage.getItem(warningKey);
                            if (savedTimestamp) {
                                console.log('[Polling] Found trial-to-paid flag, showing warning');
                                setKeysChangedTimestamp(parseInt(savedTimestamp));
                            }
                        }

                        return; // Остановить polling
                    }
                }

                // Продолжить polling если еще не достигли лимита
                if (attempts < maxAttempts) {
                    setTimeout(poll, 2000); // Следующая попытка через 2 секунды
                } else {
                    console.log('[Polling] Max attempts reached');
                    showAlert('⚠️ Подписка создается. Обновите страницу через минуту.');
                }
            } catch (error) {
                console.error('[Polling] Error:', error);
                // Продолжить polling даже при ошибке
                if (attempts < maxAttempts) {
                    setTimeout(poll, 2000);
                }
            }
        };

        // Начать polling через 2 секунды
        setTimeout(poll, 2000);
    };

    const copyToClipboard = (text: string) => {
        navigator.clipboard.writeText(text);
        hapticFeedback('light');
        showAlert('Скопировано!');
    };

    const sendAmneziaConfig = async (configNumber: number) => {
        if (!userFriendlyAddress || !activeSubscription) {
            showAlert('Нет активной подписки');
            return;
        }

        const clients = activeSubscription.amneziaClients as any[];
        if (!clients || !Array.isArray(clients) || clients.length < configNumber) {
            showAlert('Конфигурация AmneziaWG недоступна');
            return;
        }

        try {
            hapticFeedback('medium');
            const result = await api.subscriptions.sendAmneziaConfig(
                userFriendlyAddress,
                activeSubscription.id,
                configNumber
            );
            console.log('Send config result:', result);

            if (result.success && result.data?.success) {
                showAlert(`Файл конфигурации ${configNumber} отправлен в бот`);
            } else {
                showAlert('Ошибка: неизвестный ответ от сервера');
            }
        } catch (error) {
            console.error('Send config error:', error);
            showAlert('Ошибка при отправке конфигурации');
        }
    };

    // Убрали блокирующий экран загрузки - показываем UI сразу
    // Данные будут подгружаться асинхронно

    const subscriptionStatus = activeSubscription
        ? activeSubscription.status === 'PENDING'
            ? 'pending'
            : activeSubscription.planId === 'trial'
            ? 'trial'
            : 'active'
        : 'inactive';

    // Проверяем наличие активной платной подписки (не trial)
    const hasPaidSubscription = activeSubscription &&
        activeSubscription.status === 'ACTIVE' &&
        activeSubscription.planId !== 'trial';

    const daysRemaining = activeSubscription
        ? Math.max(
              1,
              Math.ceil(
                  (new Date(activeSubscription.expiresAt).getTime() -
                      Date.now()) /
                      (1000 * 60 * 60 * 24)
              )
          )
        : 0;

    // Логирование для отладки статуса подписки
    if (activeSubscription) {
        console.log('[SubscriptionStatus] Active subscription:', {
            planId: activeSubscription.planId,
            planName: activeSubscription.planName,
            status: subscriptionStatus,
            expiresAt: activeSubscription.expiresAt,
            daysRemaining,
        });
    }

    return (
        <div className="min-h-screen bg-dark-900 text-white overflow-y-auto custom-scrollbar">
            {/* Header */}
            <div className="bg-gradient-to-b from-ton/20 to-dark-900 p-4 pt-8">
                <div className="flex justify-between items-center mb-6">
                    <div className="flex items-center gap-2">
                        <Shield className="w-6 h-6 text-ton" />
                        <div className="flex items-start gap-1">
                            <span className="font-bold text-lg">WEB3 VPN</span>
                            <span style={{ fontSize: '12px', color: '#9e9e9e', lineHeight: '1', marginTop: '2px' }}>v1.5</span>
                        </div>
                    </div>

                    <TonConnectButton />
                </div>

                {isConnected && walletMismatch ? (
                    <div className="flex items-center justify-center py-12 px-4">
                        <div className="bg-yellow-900/20 border border-yellow-500/40 rounded-2xl p-6 max-w-sm w-full text-center">
                            <div className="text-4xl mb-4">⚠️</div>
                            <h2 className="text-yellow-400 font-bold text-lg mb-3">
                                Неверный кошелёк
                            </h2>
                            <p className="text-gray-300 text-sm mb-4">
                                Этот Telegram-аккаунт привязан к другому кошельку. Авторизуйтесь через:
                            </p>
                            <div className="bg-dark-800 rounded-xl px-4 py-3 mb-5 break-all">
                                <span className="text-white font-mono text-xs">{expectedWallet}</span>
                            </div>
                            <p className="text-gray-500 text-xs">
                                Отключите текущий кошелёк и подключите указанный выше
                            </p>
                        </div>
                    </div>
                ) : !isConnected ? (
                    /* Placeholder for disconnected state */
                    <div className="bg-dark-800/50 border-2 border-dashed border-dark-700 rounded-2xl p-8 flex flex-col items-center justify-center text-center my-4 animate-in fade-in zoom-in duration-300">
                        <div className="bg-dark-700 p-4 rounded-full mb-3">
                            <Wallet className="w-8 h-8 text-gray-500" />
                        </div>
                        <p className="text-gray-300 text-sm font-medium mb-4">
                            Для начала работы подключите TON кошелек
                        </p>
                        <p className="text-red-400 text-sm font-bold bg-red-500/10 px-3 py-2 rounded-lg border border-red-500/20 leading-tight">
                            Внимание! Вывод TON с баланса НЕ предусмотрен.
                        </p>
                    </div>
                ) : (
                    /* Connected State */
                    <div className="animate-in fade-in slide-in-from-top-4 duration-500">
                        {/* Balance Card */}
                        <div className="bg-gradient-to-r from-indigo-900 to-purple-900 border border-indigo-500/30 rounded-2xl p-4 mb-3 shadow-lg relative overflow-hidden">
                            <div className="absolute -right-4 -bottom-6 w-32 h-32 opacity-10 rotate-[-15deg]">
                                <Wallet className="w-full h-full text-white" />
                            </div>

                            <div className="relative z-10">
                                <div className="text-xs font-medium text-indigo-200/60 uppercase tracking-wider mb-2">
                                    Ваш баланс
                                </div>
                                <div className="mb-3">
                                    <span className="text-2xl font-bold text-white tracking-wide flex items-center gap-2">
                                        {Number(balance).toFixed(2)}
                                        <img
                                            src={TON_LOGO}
                                            alt="TON"
                                            className="w-6 h-6 rounded-full"
                                        />
                                    </span>
                                </div>
                                <div className="text-xs font-medium text-indigo-200/60 uppercase tracking-wider mb-2">
                                    Пополнить баланс
                                </div>
                                <div className="grid grid-cols-3 gap-2">
                                    {[2, 5, 9].map((amount) => (
                                        <button
                                            key={amount}
                                            onClick={() => handleDeposit(amount)}
                                            disabled={sendingTx}
                                            className="bg-white/90 text-indigo-900 py-2.5 rounded-xl text-sm font-bold hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {sendingTx ? '...' : `${amount} TON`}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Subscription Status */}
                        <div
                            className={`relative overflow-hidden rounded-2xl p-5 border shadow-lg transition-colors ${
                                subscriptionStatus === 'pending'
                                    ? 'border-blue-500/30 bg-gradient-to-br from-blue-900/40 via-dark-800 to-dark-900'
                                    : subscriptionStatus === 'trial'
                                    ? 'border-yellow-500/30 bg-gradient-to-br from-yellow-900/40 via-dark-800 to-dark-900'
                                    : subscriptionStatus === 'active'
                                    ? 'border-green-500/20 bg-gradient-to-br from-emerald-900/40 via-dark-800 to-dark-900'
                                    : 'border-dark-700 bg-dark-800'
                            }`}
                        >
                            <div className="absolute -right-4 -bottom-6 w-32 h-32 opacity-10 rotate-[-15deg]">
                                {subscriptionStatus === 'pending' ? (
                                    <Clock className="w-full h-full text-blue-400 animate-spin" />
                                ) : subscriptionStatus === 'trial' ? (
                                    <Clock className="w-full h-full text-yellow-400" />
                                ) : subscriptionStatus === 'active' ? (
                                    <Zap className="w-full h-full text-green-400" />
                                ) : (
                                    <AlertCircle className="w-full h-full text-gray-500" />
                                )}
                            </div>

                            <div className="relative z-10">
                                <div
                                    className={`text-xs font-medium uppercase tracking-wider mb-2 ${
                                        subscriptionStatus === 'pending'
                                            ? 'text-blue-200/60'
                                            : subscriptionStatus === 'trial'
                                            ? 'text-yellow-200/60'
                                            : subscriptionStatus === 'active'
                                            ? 'text-green-200/60'
                                            : 'text-gray-400'
                                    }`}
                                >
                                    Статус подписки
                                </div>

                                {subscriptionStatus === 'pending' ? (
                                    <div className="flex flex-col">
                                        <span className="text-2xl font-bold text-white flex items-center gap-3">
                                            Создаётся...
                                            <Clock className="w-6 h-6 text-blue-500 animate-spin" />
                                        </span>
                                        <div className="mt-2 text-sm text-blue-100/70 flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>
                                            Настройка VPN-конфигурации
                                        </div>
                                    </div>
                                ) : subscriptionStatus === 'active' ? (
                                    <div className="flex flex-col">
                                        <span className="text-3xl font-bold text-white flex items-center gap-3">
                                            Активна
                                            <Clock
                                                className="w-6 h-6"
                                                style={{ color: '#35b175' }}
                                            />
                                        </span>
                                        <div
                                            className="mt-2 text-sm flex items-center gap-2"
                                            style={{ color: '#35b175' }}
                                        >
                                            <div
                                                className="w-2 h-2 rounded-full animate-pulse"
                                                style={{
                                                    backgroundColor: '#35b175',
                                                }}
                                            ></div>
                                            Осталось: {daysRemaining}{' '}
                                            {daysRemaining === 1
                                                ? 'день'
                                                : daysRemaining < 5
                                                ? 'дня'
                                                : 'дней'}
                                        </div>
                                    </div>
                                ) : subscriptionStatus === 'trial' ? (
                                    <div className="flex flex-col">
                                        <span className="text-2xl font-bold text-white flex items-center gap-3">
                                            Пробная на 3 дня
                                            <Clock className="w-6 h-6 text-yellow-500" />
                                        </span>
                                        <div className="mt-2 text-sm text-yellow-100/70 flex items-center gap-2">
                                            <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></div>
                                            Осталось: {daysRemaining} дня
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col">
                                        <span className="text-xl font-bold text-gray-400">
                                            Не активна
                                        </span>
                                        <span className="text-xs text-gray-500 mt-1">
                                            Выберите тариф ниже для подключения
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Access Keys (shown when subscribed and ACTIVE) */}
                        {/* Warning: Keys Changed (Trial → Paid) */}
                        {showKeysChangedWarning && (subscriptionStatus === 'active' || subscriptionStatus === 'trial') && activeSubscription && (
                            <div className="mt-4 bg-red-900/20 border-2 border-red-500/50 rounded-2xl p-4 shadow-lg animate-in fade-in slide-in-from-top-4 duration-500">
                                <div className="flex items-start gap-3">
                                    <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <h4 className="text-red-300 text-sm font-bold mb-2">
                                            Внимание!
                                        </h4>
                                        <p className="text-red-200/80 text-xs leading-relaxed">
                                            Ключи доступа изменились! Не забудьте добавить новые ключи VLESS и AmneziaWG в приложения подключения к VPN.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}

                        {(subscriptionStatus === 'active' ||
                            subscriptionStatus === 'trial') &&
                            activeSubscription && (
                                <div className="mt-4 bg-dark-800 border border-dark-700 rounded-2xl p-4 shadow-lg animate-in fade-in slide-in-from-top-4 duration-500">
                                    <h4 className="text-white text-sm font-bold mb-3">
                                        Ваши ключи доступа
                                    </h4>

                                    {/* Protocol Tabs */}
                                    <div className="flex flex-wrap gap-1 bg-dark-900 rounded-lg p-1 mb-3">
                                        <button
                                            onClick={() => setKeyTab('vless')}
                                            className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                                                keyTab === 'vless'
                                                    ? 'bg-dark-700 text-white shadow'
                                                    : 'text-gray-400 hover:text-gray-200'
                                            }`}
                                        >
                                            VLESS
                                        </button>
                                        <button
                                            onClick={() => setKeyTab('amnezia')}
                                            className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                                                keyTab === 'amnezia'
                                                    ? 'bg-dark-700 text-white shadow'
                                                    : 'text-gray-400 hover:text-gray-200'
                                            }`}
                                        >
                                            AmneziaWG
                                        </button>
                                        <button
                                            onClick={() => setKeyTab('hysteria2')}
                                            className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                                                keyTab === 'hysteria2'
                                                    ? 'bg-dark-700 text-white shadow'
                                                    : 'text-gray-400 hover:text-gray-200'
                                            }`}
                                        >
                                            Hysteria2
                                        </button>
                                        <button
                                            onClick={() => setKeyTab('trojan')}
                                            className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                                                keyTab === 'trojan'
                                                    ? 'bg-dark-700 text-white shadow'
                                                    : 'text-gray-400 hover:text-gray-200'
                                            }`}
                                        >
                                            Trojan
                                        </button>
                                    </div>

                                    {/* VLESS Content */}
                                    {keyTab === 'vless' && (
                                        <div className="animate-in fade-in duration-300">
                                            <div className="bg-blue-500/5 p-2 rounded-lg border border-blue-500/10 mb-2 text-[11px] text-blue-200 text-center font-medium">
                                                1 ключ на 5 устройств
                                            </div>
                                            <div className="bg-black/30 p-2 rounded text-[10px] break-all font-mono text-gray-300 border border-white/5 mb-2">
                                                {activeSubscription.vlessKey}
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() =>
                                                        copyToClipboard(
                                                            activeSubscription.vlessKey
                                                        )
                                                    }
                                                    className="flex-1 flex justify-center items-center gap-2 text-xs text-green-400 bg-green-500/10 py-2.5 rounded-lg hover:bg-green-500/20 transition-colors font-medium border border-green-500/20"
                                                >
                                                    <Copy className="w-3.5 h-3.5" />{' '}
                                                    Скопировать
                                                </button>
                                                {activeSubscription.vlessQrCode && (
                                                    <button
                                                        onClick={() => setShowVlessQr(!showVlessQr)}
                                                        className="flex items-center justify-center gap-1.5 text-xs text-blue-400 bg-blue-500/10 px-3 py-2.5 rounded-lg hover:bg-blue-500/20 transition-colors font-medium border border-blue-500/20"
                                                    >
                                                        <QrCode className="w-3.5 h-3.5" />
                                                        QR код
                                                    </button>
                                                )}
                                            </div>
                                            {showVlessQr && activeSubscription.vlessQrCode && (
                                                <div className="mt-3 p-3 bg-white rounded-lg relative">
                                                    <button
                                                        onClick={() => setShowVlessQr(false)}
                                                        className="absolute top-2 right-2 p-1 bg-gray-200 hover:bg-gray-300 rounded-full transition-colors"
                                                    >
                                                        <X className="w-4 h-4 text-gray-700" />
                                                    </button>
                                                    <img
                                                        src={activeSubscription.vlessQrCode}
                                                        alt="VLESS QR Code"
                                                        className="w-full h-auto"
                                                    />
                                                    <p className="text-center text-[10px] text-gray-700 mt-2">
                                                        Отсканируйте для подключения
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* AmneziaWG Content */}
                                    {keyTab === 'amnezia' && (
                                        <div className="animate-in fade-in duration-300">
                                            <>
                                                <div className="bg-red-500/10 p-2.5 rounded-lg border border-red-500/20 mb-3 text-center">
                                                    <p className="text-red-400 text-[13px] font-bold">
                                                        ⚠️ Внимание! 1 ключ = 1 устройство
                                                    </p>
                                                </div>
                                                <div className="bg-blue-500/5 p-2.5 rounded-lg border border-blue-500/10 mb-3 text-[10px] text-blue-200">
                                                    Получите файлы конфигурации
                                                    AmneziaWG в боте, скачайте
                                                    их на устройства и
                                                    импортируйте в приложении.
                                                    Каждый файл — для отдельного устройства.
                                                </div>
                                                {activeSubscription.status === 'PENDING' ||
                                                !(activeSubscription.amneziaClients && Array.isArray(activeSubscription.amneziaClients) && activeSubscription.amneziaClients.length === 5) ? (
                                                    <div className="w-full flex justify-center items-center gap-2 text-xs py-2.5 rounded-lg text-gray-500 bg-gray-500/10 border border-gray-500/20">
                                                        <div className="w-3.5 h-3.5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
                                                        Создание конфигураций...
                                                    </div>
                                                ) : (
                                                    <div className="space-y-2">
                                                        {[1, 2, 3, 4, 5].map((num) => {
                                                            const client = activeSubscription.amneziaClients?.[num - 1];
                                                            return (
                                                                <div key={num}>
                                                                    <div className="flex gap-2">
                                                                        <button
                                                                            onClick={() => sendAmneziaConfig(num)}
                                                                            className="flex-1 flex justify-center items-center gap-2 text-xs text-blue-400 bg-blue-500/10 py-2.5 rounded-lg hover:bg-blue-500/20 transition-colors font-medium border border-blue-500/20"
                                                                        >
                                                                            <Download className="w-3.5 h-3.5" />
                                                                            Получить конфигурацию {num}
                                                                        </button>
                                                                        {client?.qrCode && (
                                                                            <button
                                                                                onClick={() => setShowAmneziaQr(showAmneziaQr === num ? null : num)}
                                                                                className="flex items-center justify-center gap-1.5 text-xs text-purple-400 bg-purple-500/10 px-3 py-2.5 rounded-lg hover:bg-purple-500/20 transition-colors font-medium border border-purple-500/20"
                                                                            >
                                                                                <QrCode className="w-3.5 h-3.5" />
                                                                                QR код
                                                                            </button>
                                                                        )}
                                                                    </div>
                                                                    {showAmneziaQr === num && client?.qrCode && (
                                                                        <div className="mt-2 p-3 bg-white rounded-lg relative">
                                                                            <button
                                                                                onClick={() => setShowAmneziaQr(null)}
                                                                                className="absolute top-2 right-2 p-1 bg-gray-200 hover:bg-gray-300 rounded-full transition-colors"
                                                                            >
                                                                                <X className="w-4 h-4 text-gray-700" />
                                                                            </button>
                                                                            <img
                                                                                src={client.qrCode}
                                                                                alt={`AmneziaWG QR Code ${num}`}
                                                                                className="w-full h-auto"
                                                                            />
                                                                            <p className="text-center text-[10px] text-gray-700 mt-2">
                                                                                Отсканируйте для подключения
                                                                            </p>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </>
                                        </div>
                                    )}

                                    {/* Hysteria2 Content */}
                                    {keyTab === 'hysteria2' && (
                                        <div className="animate-in fade-in duration-300">
                                            {!activeSubscription.singboxClients ? (
                                                <div className="w-full flex justify-center items-center gap-2 text-xs py-2.5 rounded-lg text-gray-500 bg-gray-500/10 border border-gray-500/20">
                                                    <div className="w-3.5 h-3.5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
                                                    Создание ключей...
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="bg-blue-500/5 p-2 rounded-lg border border-blue-500/10 mb-2 text-[11px] text-blue-200 text-center font-medium">
                                                        1 ключ на 5 устройств
                                                    </div>
                                                    <div className="bg-black/30 p-2 rounded text-[10px] break-all font-mono text-gray-300 border border-white/5 mb-2">
                                                        {activeSubscription.singboxClients.hysteria2Key}
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => copyToClipboard(activeSubscription.singboxClients!.hysteria2Key)}
                                                            className="flex-1 flex justify-center items-center gap-2 text-xs text-green-400 bg-green-500/10 py-2.5 rounded-lg hover:bg-green-500/20 transition-colors font-medium border border-green-500/20"
                                                        >
                                                            <Copy className="w-3.5 h-3.5" />
                                                            Скопировать
                                                        </button>
                                                        {activeSubscription.singboxClients.hysteria2QrCode && (
                                                            <button
                                                                onClick={() => setShowHysteria2Qr(!showHysteria2Qr)}
                                                                className="flex items-center justify-center gap-1.5 text-xs text-blue-400 bg-blue-500/10 px-3 py-2.5 rounded-lg hover:bg-blue-500/20 transition-colors font-medium border border-blue-500/20"
                                                            >
                                                                <QrCode className="w-3.5 h-3.5" />
                                                                QR код
                                                            </button>
                                                        )}
                                                    </div>
                                                    {showHysteria2Qr && activeSubscription.singboxClients.hysteria2QrCode && (
                                                        <div className="mt-3 p-3 bg-white rounded-lg relative">
                                                            <button
                                                                onClick={() => setShowHysteria2Qr(false)}
                                                                className="absolute top-2 right-2 p-1 bg-gray-200 hover:bg-gray-300 rounded-full transition-colors"
                                                            >
                                                                <X className="w-4 h-4 text-gray-700" />
                                                            </button>
                                                            <img
                                                                src={activeSubscription.singboxClients.hysteria2QrCode}
                                                                alt="Hysteria2 QR Code"
                                                                className="w-full h-auto"
                                                            />
                                                            <p className="text-center text-[10px] text-gray-700 mt-2">
                                                                Отсканируйте для подключения
                                                            </p>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}

                                    {/* Trojan Content */}
                                    {keyTab === 'trojan' && (
                                        <div className="animate-in fade-in duration-300">
                                            {!activeSubscription.singboxClients ? (
                                                <div className="w-full flex justify-center items-center gap-2 text-xs py-2.5 rounded-lg text-gray-500 bg-gray-500/10 border border-gray-500/20">
                                                    <div className="w-3.5 h-3.5 border-2 border-gray-500 border-t-transparent rounded-full animate-spin"></div>
                                                    Создание ключей...
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="bg-blue-500/5 p-2 rounded-lg border border-blue-500/10 mb-2 text-[11px] text-blue-200 text-center font-medium">
                                                        1 ключ на 5 устройств
                                                    </div>
                                                    <div className="bg-black/30 p-2 rounded text-[10px] break-all font-mono text-gray-300 border border-white/5 mb-2">
                                                        {activeSubscription.singboxClients.trojanKey}
                                                    </div>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => copyToClipboard(activeSubscription.singboxClients!.trojanKey)}
                                                            className="flex-1 flex justify-center items-center gap-2 text-xs text-green-400 bg-green-500/10 py-2.5 rounded-lg hover:bg-green-500/20 transition-colors font-medium border border-green-500/20"
                                                        >
                                                            <Copy className="w-3.5 h-3.5" />
                                                            Скопировать
                                                        </button>
                                                        {activeSubscription.singboxClients.trojanQrCode && (
                                                            <button
                                                                onClick={() => setShowTrojanQr(!showTrojanQr)}
                                                                className="flex items-center justify-center gap-1.5 text-xs text-blue-400 bg-blue-500/10 px-3 py-2.5 rounded-lg hover:bg-blue-500/20 transition-colors font-medium border border-blue-500/20"
                                                            >
                                                                <QrCode className="w-3.5 h-3.5" />
                                                                QR код
                                                            </button>
                                                        )}
                                                    </div>
                                                    {showTrojanQr && activeSubscription.singboxClients.trojanQrCode && (
                                                        <div className="mt-3 p-3 bg-white rounded-lg relative">
                                                            <button
                                                                onClick={() => setShowTrojanQr(false)}
                                                                className="absolute top-2 right-2 p-1 bg-gray-200 hover:bg-gray-300 rounded-full transition-colors"
                                                            >
                                                                <X className="w-4 h-4 text-gray-700" />
                                                            </button>
                                                            <img
                                                                src={activeSubscription.singboxClients.trojanQrCode}
                                                                alt="Trojan QR Code"
                                                                className="w-full h-auto"
                                                            />
                                                            <p className="text-center text-[10px] text-gray-700 mt-2">
                                                                Отсканируйте для подключения
                                                            </p>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                    </div>
                )}
            </div>

            {/* Tabs */}
            <div className="flex px-4 border-b border-dark-700">
                <button
                    onClick={() => setActiveTab('main')}
                    className={`flex-1 pb-3 text-sm font-medium ${
                        activeTab === 'main'
                            ? 'text-ton border-b-2 border-ton'
                            : 'text-gray-400'
                    }`}
                >
                    Тарифы
                </button>
                <button
                    onClick={() => isConnected && setActiveTab('referral')}
                    disabled={!isConnected}
                    className={`flex-1 pb-3 text-sm font-medium transition-colors relative ${
                        activeTab === 'referral'
                            ? 'text-ton border-b-2 border-ton'
                            : 'text-gray-400'
                    } ${
                        !isConnected
                            ? 'opacity-50 cursor-not-allowed'
                            : 'hover:text-gray-200'
                    }`}
                >
                    <div className="flex items-center justify-center gap-1">
                        Рефералы
                        {!isConnected && <Lock className="w-3 h-3" />}
                    </div>
                </button>
                <button
                    onClick={() => setActiveTab('instructions')}
                    className={`flex-1 pb-3 text-sm font-medium ${
                        activeTab === 'instructions'
                            ? 'text-ton border-b-2 border-ton'
                            : 'text-gray-400'
                    }`}
                >
                    Инструкции
                </button>
            </div>

            {/* Tab Content */}
            <div className="p-4 pb-20">
                {activeTab === 'main' && (
                    <div className="space-y-3">
                        {/* Trial Subscription Block */}
                        {(() => {
                            console.log('[TrialBlock] Rendering check - trialEligible:', trialEligible);
                            return null;
                        })()}
                        {/* ВРЕМЕННО: пробная подписка скрыта на время тех. работ */}
                        {trialEligible && false && (
                            <div className="bg-gradient-to-br from-yellow-900/40 via-dark-800 to-dark-900 border border-yellow-500/30 rounded-xl p-4 shadow-lg">
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-bold text-lg text-yellow-200 flex items-center gap-2">
                                        <Clock className="w-5 h-5" />
                                        Пробная подписка
                                    </h3>
                                    <span className="bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded text-[10px] font-medium tracking-tight border border-yellow-500/30">
                                        БЕСПЛАТНО
                                    </span>
                                </div>
                                <p className="text-sm text-yellow-100/80 mb-3">
                                    Попробуйте сервис бесплатно в течение 3 дней
                                </p>
                                <button
                                    onClick={handleActivateTrial}
                                    disabled={activatingTrial}
                                    className="w-full bg-yellow-500 hover:bg-yellow-400 text-dark-900 py-2.5 rounded-lg font-bold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                    {activatingTrial ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-dark-900 border-t-transparent rounded-full animate-spin"></div>
                                            Активация...
                                        </>
                                    ) : (
                                        <>
                                            <Zap className="w-4 h-4" />
                                            Активировать пробную подписку
                                        </>
                                    )}
                                </button>
                                <div className="mt-2 text-[10px] text-yellow-200/60">
                                    🚀 ∞ трафик &nbsp;⚡ up to 100Mbps &nbsp;🔒 5 online IP limit
                                </div>
                            </div>
                        )}

                        {plans.map((plan) => {
                            const canAfford = balance >= plan.price;
                            const showBuyButton = isConnected && canAfford;

                            return (
                                <div
                                    key={plan.id}
                                    className={`bg-dark-800 border border-dark-700 rounded-xl p-4 hover:border-ton/50 transition-colors ${
                                        !isConnected ? 'opacity-70' : ''
                                    }`}
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <h3 className="font-bold text-lg">
                                            {plan.name}
                                        </h3>
                                        <span className="bg-ton/10 text-ton px-2 py-0.5 rounded text-[10px] font-medium tracking-tight">
                                            VLESS / AmneziaWG / Hysteria2 / Trojan
                                        </span>
                                    </div>
                                    <div className="flex justify-between items-end">
                                        <div className="text-2xl font-bold text-white flex items-center gap-1">
                                            {plan.price}
                                            <img
                                                src={TON_LOGO}
                                                alt="M"
                                                className="w-5 h-5 rounded-full"
                                            />
                                        </div>
                                        {showBuyButton ? (
                                            <button
                                                onClick={() =>
                                                    handleBuyPlan(plan.id)
                                                }
                                                className="px-6 py-2 rounded-lg text-sm font-bold bg-white text-black hover:bg-gray-200 transition-colors"
                                            >
                                                Купить
                                            </button>
                                        ) : (
                                            <button
                                                onClick={() => {
                                                    if (!isConnected) {
                                                        showAlert(
                                                            'Подключите кошелек'
                                                        );
                                                    } else {
                                                        handleDeposit(plan.price);
                                                    }
                                                }}
                                                disabled={sendingTx}
                                                className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                                    isConnected
                                                        ? 'bg-dark-700 text-gray-400 hover:bg-dark-600'
                                                        : 'bg-ton text-white hover:bg-ton-dark'
                                                }`}
                                            >
                                                {sendingTx
                                                    ? '...'
                                                    : !isConnected
                                                    ? 'Подключить'
                                                    : 'Пополнить'}
                                            </button>
                                        )}
                                    </div>
                                    <div className="mt-2 text-[10px] text-gray-500 whitespace-nowrap overflow-hidden text-ellipsis">
                                        🚀 ∞ трафик &nbsp;⚡ up to 100Mbps
                                        &nbsp;🔒 5 online IP limit
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {activeTab === 'referral' && (
                    <ReferralTab
                        walletAddress={userFriendlyAddress}
                        user={user}
                    />
                )}
                {activeTab === 'instructions' && <InstructionsTab />}

                {/* Support Block - показывается только при активной платной подписке */}
                {hasPaidSubscription && (
                    <div className="mt-6 bg-gradient-to-br from-blue-900/30 to-dark-800 border border-blue-500/30 rounded-xl p-4 shadow-lg">
                        <div className="flex items-center gap-3 mb-2">
                            <MessageCircle className="w-5 h-5 text-blue-400" />
                            <h3 className="font-bold text-white">
                                Техническая поддержка
                            </h3>
                        </div>
                        <p className="text-sm text-gray-300 mb-3">
                            Возникли вопросы или проблемы? Наша поддержка готова помочь!
                        </p>
                        <a
                            href="https://t.me/web3_vpn_support_bot"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full bg-blue-600 hover:bg-blue-500 text-white py-2.5 rounded-lg font-medium text-sm transition-colors text-center"
                        >
                            Написать в поддержку
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}

function ReferralTab({
    walletAddress,
    user,
}: {
    walletAddress?: string;
    user: any;
}) {
    const [stats, setStats] = useState<any>(null);
    const { showAlert, hapticFeedback } = useTelegramWebApp();

    useEffect(() => {
        if (walletAddress) {
            loadReferralStats();

            // Auto-refresh every 10 seconds
            const interval = setInterval(() => {
                loadReferralStats();
            }, 10000);

            return () => clearInterval(interval);
        }
    }, [walletAddress]);

    const loadReferralStats = async () => {
        if (!walletAddress) return;

        try {
            const result = await api.referral.getStats(walletAddress);
            if (result.success) {
                setStats(result.data);
            }
        } catch (error) {
            console.error('Failed to load referral stats');
        }
    };

    const copyReferralLink = () => {
        if (stats?.referralLink) {
            navigator.clipboard.writeText(stats.referralLink);
            hapticFeedback('success');
            showAlert('Реферальная ссылка скопирована!');
        }
    };

    return (
        <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
            <div className="bg-gradient-to-br from-purple-900/50 to-dark-800 p-5 rounded-xl border border-purple-500/30">
                <div className="flex items-center gap-3 mb-4">
                    <Users className="w-6 h-6 text-purple-400" />
                    <h3 className="font-bold">Партнерская программа</h3>
                </div>

                {stats && (
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="bg-black/30 p-3 rounded-lg">
                            <div className="text-xs text-gray-400">
                                Приглашено
                            </div>
                            <div className="text-xl font-bold text-white">
                                {stats.totalReferrals}
                            </div>
                        </div>
                        <div className="bg-black/30 p-3 rounded-lg">
                            <div className="text-xs text-gray-400">
                                Общий доход
                            </div>
                            <div className="text-xl font-bold text-white flex items-center gap-1">
                                {Number(stats.totalEarned).toFixed(2)}
                                <img
                                    src={TON_LOGO}
                                    alt="M"
                                    className="w-5 h-5 rounded-full"
                                />
                            </div>
                        </div>
                    </div>
                )}

                <p className="text-xs text-gray-300 mb-4">
                    Получи 50% с первой оплаты каждого приглашенного
                    пользователя. Вознаграждения начисляются на ваш внутренний
                    баланс.
                </p>

                <button
                    onClick={copyReferralLink}
                    disabled={!stats?.referralLink || !user?.tgId}
                    className="w-full bg-purple-600 hover:bg-purple-500 text-white py-2 rounded-lg font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    Пригласить друга
                </button>

                {!user?.tgId && (
                    <p className="text-xs text-gray-400 mt-2 text-center">
                        Реферальная программа доступна только при входе через
                        Telegram Mini App
                    </p>
                )}
            </div>

            {stats?.rewardHistory && stats.rewardHistory.length > 0 && (
                <div className="bg-dark-800 rounded-xl p-4 border border-dark-700">
                    <h4 className="font-bold text-sm mb-3">
                        История начислений
                    </h4>
                    {stats.rewardHistory.map((reward: any, i: number) => {
                        const dateStr = new Date(
                            reward.createdAt
                        ).toLocaleString('ru-RU', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                            second: '2-digit',
                        });

                        // Форматируем адрес кошелька: UQCd...lP7B
                        const formatWallet = (wallet: string) => {
                            if (!wallet || wallet.length < 8) return 'Unknown';
                            return `${wallet.slice(0, 4)}...${wallet.slice(
                                -4
                            )}`;
                        };

                        return (
                            <div
                                key={i}
                                className="flex justify-between items-center py-2 border-b border-dark-700 last:border-0"
                            >
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-full bg-purple-700/50 flex items-center justify-center text-xs">
                                        {i + 1}
                                    </div>
                                    <div className="text-xs">
                                        <div className="text-white">
                                            {formatWallet(reward.refereeWallet)}
                                        </div>
                                        <div className="text-gray-500 text-[10px]">
                                            {dateStr}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-green-400 text-sm font-bold flex items-center gap-1">
                                    +{Number(reward.amount).toFixed(2)}
                                    <img
                                        src={TON_LOGO}
                                        alt="M"
                                        className="w-4 h-4 rounded-full"
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function InstructionsTab() {
    const [platformTab, setPlatformTab] = useState<'android' | 'ios' | 'windows' | 'macos'>(
        'android'
    );

    return (
        <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
            {/* Platform Tabs */}
            <div className="bg-dark-800 border border-dark-700 rounded-2xl p-4 shadow-lg">
                <h4 className="text-white text-sm font-bold mb-3">
                    Инструкции по подключению
                </h4>

                {/* Platform Switcher */}
                <div className="flex bg-dark-900 rounded-lg p-1 mb-4">
                    <button
                        onClick={() => setPlatformTab('android')}
                        className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                            platformTab === 'android'
                                ? 'bg-dark-700 text-white shadow'
                                : 'text-gray-400 hover:text-gray-200'
                        }`}
                    >
                        Android
                    </button>
                    <button
                        onClick={() => setPlatformTab('ios')}
                        className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                            platformTab === 'ios'
                                ? 'bg-dark-700 text-white shadow'
                                : 'text-gray-400 hover:text-gray-200'
                        }`}
                    >
                        iOS
                    </button>
                    <button
                        onClick={() => setPlatformTab('windows')}
                        className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                            platformTab === 'windows'
                                ? 'bg-dark-700 text-white shadow'
                                : 'text-gray-400 hover:text-gray-200'
                        }`}
                    >
                        Windows
                    </button>
                    <button
                        onClick={() => setPlatformTab('macos')}
                        className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                            platformTab === 'macos'
                                ? 'bg-dark-700 text-white shadow'
                                : 'text-gray-400 hover:text-gray-200'
                        }`}
                    >
                        MacOS
                    </button>
                </div>

                {/* Android Instructions */}
                {platformTab === 'android' && (
                    <div className="space-y-4">
                        {/* VLESS Instructions */}
                        <div className="bg-dark-900/50 rounded-lg p-3 border border-dark-700">
                            <h5 className="text-white text-xs font-bold mb-2 flex items-center gap-2">
                                <Lock className="w-3 h-3" />
                                VLESS / Hysteria2 / Trojan (V2BOX)
                            </h5>
                            <div className="space-y-2 text-[11px] text-gray-300">
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        1
                                    </div>
                                    <p>
                                        Установите <b>V2BOX</b> из{' '}
                                        <a
                                            href="https://play.google.com/store/apps/details?id=dev.hexasoftware.v2box"
                                            target="_blank"
                                            className="text-blue-400 underline"
                                        >
                                            Google Play
                                        </a>
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        2
                                    </div>
                                    <p>
                                        Скопируйте VLESS / Hysteria2 / Trojan ключ в блоке "Ваши ключи доступа"
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        3
                                    </div>
                                    <p>Откройте V2BOX</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        4
                                    </div>
                                    <p>Перейдите на вкладку Configs (Конфигурации) в нижней части экрана</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        5
                                    </div>
                                    <p>Нажмите "+" (сверху справа)</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        6
                                    </div>
                                    <p>Импортировать v2ray из буфера</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        7
                                    </div>
                                    <p>В списке конфигураций появится новая конфигурация, выберите её нажатием</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        8
                                    </div>
                                    <p>В нижней части экрана приложения нажмите "Подключиться"</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        9
                                    </div>
                                    <p>В процессе установки приложения, добавления конфигурации и подключения, на все системные уведомления и предупреждения отвечайте утвердительно и выдавайте разрешение</p>
                                </div>
                            </div>
                        </div>

                        {/* AmneziaWG Instructions */}
                        <div className="bg-dark-900/50 rounded-lg p-3 border border-dark-700">
                            <h5 className="text-white text-xs font-bold mb-2 flex items-center gap-2">
                                <Shield className="w-3 h-3" />
                                AmneziaWG
                            </h5>
                            <div className="space-y-2 text-[11px] text-gray-300">
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        1
                                    </div>
                                    <p>
                                        Установите <b>AmneziaWG</b> из{' '}
                                        <a
                                            href="https://play.google.com/store/apps/details?id=org.amnezia.awg"
                                            target="_blank"
                                            className="text-blue-400 underline"
                                        >
                                            Google Play
                                        </a>
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        2
                                    </div>
                                    <p>
                                        Нажмите "Получить файл конфигурации" на
                                        вкладке AmneziaWG в блоке "Ваши ключи
                                        доступа"
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        3
                                    </div>
                                    <p>
                                        Файл конфигурации будет отправлен вам в
                                        Telegram бот
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        4
                                    </div>
                                    <p>
                                        Сохраните файл конфигурации на
                                        устройстве
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        5
                                    </div>
                                    <p>Откройте AmneziaWG</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        6
                                    </div>
                                    <p>Нажмите "+" (внизу справа)</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        7
                                    </div>
                                    <p>Импорт из файла или архива</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        8
                                    </div>
                                    <p>Выберите загруженный на устройство файл конфигурации</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        9
                                    </div>
                                    <p>Появится импортированный туннель</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        10
                                    </div>
                                    <p>
                                        Нажмите переключатель справа от названия
                                        туннеля
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* iOS Instructions */}
                {platformTab === 'ios' && (
                    <div className="space-y-4">
                        {/* VLESS Instructions */}
                        <div className="bg-dark-900/50 rounded-lg p-3 border border-dark-700">
                            <h5 className="text-white text-xs font-bold mb-2 flex items-center gap-2">
                                <Lock className="w-3 h-3" />
                                VLESS / Hysteria2 / Trojan (INCY)
                            </h5>
                            <div className="space-y-2 text-[11px] text-gray-300">
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        1
                                    </div>
                                    <p>
                                        Установите <b>INCY</b> из{' '}
                                        <a
                                            href="https://apps.apple.com/ru/app/incy/id6756943388"
                                            target="_blank"
                                            className="text-blue-400 underline"
                                        >
                                            App Store
                                        </a>
                                        .
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        2
                                    </div>
                                    <p>Скопируйте VLESS / Hysteria2 / Trojan ключ в блоке "Ваши ключи доступа".</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        3
                                    </div>
                                    <p>Откройте INCY.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        4
                                    </div>
                                    <p>Нажмите "+ Добавить".</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        5
                                    </div>
                                    <p>Вставьте скопированный ключ VLESS / Hysteria2 / Trojan.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        6
                                    </div>
                                    <p>Нажмите "Добавить".</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        7
                                    </div>
                                    <p>Сервер добавится на блоке "Мои серверы", выберите его нажатием.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        8
                                    </div>
                                    <p>Нажмите кнопку подключения.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        9
                                    </div>
                                    <p>В процессе установки приложения, добавления конфигурации и подключения, на все системные уведомления и предупреждения отвечайте утвердительно и выдавайте разрешение.</p>
                                </div>
                            </div>
                        </div>

                        {/* AmneziaWG Instructions */}
                        <div className="bg-dark-900/50 rounded-lg p-3 border border-dark-700">
                            <h5 className="text-white text-xs font-bold mb-2 flex items-center gap-2">
                                <Shield className="w-3 h-3" />
                                AmneziaWG
                            </h5>
                            <div className="space-y-2 text-[11px] text-gray-300">
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        1
                                    </div>
                                    <p>
                                        Установите <b>AmneziaWG</b> из{' '}
                                        <a
                                            href="https://apps.apple.com/app/amneziawg/id6476926464"
                                            target="_blank"
                                            className="text-blue-400 underline"
                                        >
                                            App Store
                                        </a>
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        2
                                    </div>
                                    <p>
                                        Нажмите "Получить файл конфигурации" на
                                        вкладке AmneziaWG в блоке "Ваши ключи
                                        доступа"
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        3
                                    </div>
                                    <p>
                                        Файл конфигурации будет отправлен вам в
                                        Telegram бот
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        4
                                    </div>
                                    <p>
                                        Сохраните файл конфигурации на
                                        устройстве
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        5
                                    </div>
                                    <p>Откройте AmneziaWG</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        6
                                    </div>
                                    <p>Нажмите "+" (сверху справа)</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        7
                                    </div>
                                    <p>Импорт из файла или архива</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        8
                                    </div>
                                    <p>Выберите загруженный на устройство файл конфигурации</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        9
                                    </div>
                                    <p>Появится импортированный туннель</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        10
                                    </div>
                                    <p>
                                        Нажмите переключатель справа от названия
                                        туннеля
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Windows Instructions */}
                {platformTab === 'windows' && (
                    <div className="space-y-4">
                        {/* VLESS Instructions */}
                        <div className="bg-dark-900/50 rounded-lg p-3 border border-dark-700">
                            <h5 className="text-white text-xs font-bold mb-2 flex items-center gap-2">
                                <Lock className="w-3 h-3" />
                                VLESS / Hysteria2 / Trojan (Hiddify)
                            </h5>
                            <div className="space-y-2 text-[11px] text-gray-300">
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        1
                                    </div>
                                    <p>
                                        Скачайте приложение <b>Hiddify</b> c{' '}
                                        <a
                                            href="https://github.com/hiddify/hiddify-app/releases/"
                                            target="_blank"
                                            className="text-blue-400 underline"
                                        >
                                            GitHub
                                        </a>
                                        {' '}(OS → Windows → Setup x64).
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        2
                                    </div>
                                    <p>Запустите Hiddify-Windows-Setup-x64.exe - установите приложение.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        3
                                    </div>
                                    <p>Откройте приложение.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        4
                                    </div>
                                    <p>На первом экране выберите Регион → Другой.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        5
                                    </div>
                                    <p>Скопируйте VLESS / Hysteria2 / Trojan ключ в блоке "Ваши ключи доступа".</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        6
                                    </div>
                                    <p>Нажмите "+" сверху справа, затем "Буфер обмена".</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        7
                                    </div>
                                    <p>Сервер добавится.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        8
                                    </div>
                                    <p>Нажмите круглую кнопку "Подключиться".</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        9
                                    </div>
                                    <p>Подключение к VPN активировано.</p>
                                </div>
                            </div>
                        </div>

                        {/* AmneziaWG Instructions */}
                        <div className="bg-dark-900/50 rounded-lg p-3 border border-dark-700">
                            <h5 className="text-white text-xs font-bold mb-2 flex items-center gap-2">
                                <Shield className="w-3 h-3" />
                                AmneziaWG (AmneziaVPN)
                            </h5>
                            <div className="space-y-2 text-[11px] text-gray-300">
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        1
                                    </div>
                                    <p>
                                        Скачать приложение{' '}
                                        <a
                                            href="https://amnezia.org/ru/downloads"
                                            target="_blank"
                                            className="text-blue-400 underline"
                                        >
                                            <b>AmneziaVPN</b>
                                        </a>
                                        , выбрав вашу операционную систему.
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        2
                                    </div>
                                    <p>Установить приложение AmneziaVPN.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        3
                                    </div>
                                    <p>Запустить приложение AmneziaVPN.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        4
                                    </div>
                                    <p>Получить файл конфигурации AmneziaWG в боте и перенести его на компьютер.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        5
                                    </div>
                                    <p>В нижней части окна приложения нажать кновку "+".</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        6
                                    </div>
                                    <p>Прокрутить окно вниз и выбрать "Файл с настройками подключения".</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        7
                                    </div>
                                    <p>В открывшемся окне выбрать файл конфигурации AmneziaWG.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        8
                                    </div>
                                    <p>Нажать кнопку "Подключиться".</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        9
                                    </div>
                                    <p>Сервер будет сохранен в списке серверов.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        10
                                    </div>
                                    <p>Нажать круглую кнопку "Подключиться".</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        11
                                    </div>
                                    <p>Дождаться установки соединения.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        12
                                    </div>
                                    <p>Подключение к VPN активировано.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* MacOS Instructions */}
                {platformTab === 'macos' && (
                    <div className="space-y-4">
                        {/* VLESS Instructions */}
                        <div className="bg-dark-900/50 rounded-lg p-3 border border-dark-700">
                            <h5 className="text-white text-xs font-bold mb-2 flex items-center gap-2">
                                <Lock className="w-3 h-3" />
                                VLESS / Hysteria2 / Trojan (HAPP - Proxy Utility)
                            </h5>
                            <div className="space-y-2 text-[11px] text-gray-300">
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        1
                                    </div>
                                    <p>
                                        Установить <b>HAPP - Proxy Utility Plus</b> из{' '}
                                        <a
                                            href="https://apps.apple.com/ru/app/happ-proxy-utility-plus/id6746188973"
                                            target="_blank"
                                            className="text-blue-400 underline"
                                        >
                                            App Store
                                        </a>
                                        .
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        2
                                    </div>
                                    <p>Запустить приложение.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        3
                                    </div>
                                    <p>Скопируйте VLESS / Hysteria2 / Trojan ключ в блоке "Ваши ключи доступа".</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        4
                                    </div>
                                    <p>Перейдите в раздел Profiles / Configs.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        5
                                    </div>
                                    <p>Нажмите Import → From Clipboard.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        6
                                    </div>
                                    <p>Сервер появится в списке, выберите его нажатием.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        7
                                    </div>
                                    <p>Нажмите переключатель Connect / Start / Enable.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        8
                                    </div>
                                    <p>Статус должен стать Connected, VPN подключен.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        9
                                    </div>
                                    <p>macOS может запросить разрешение VPN → нажмите Разрешить.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        10
                                    </div>
                                    <p>В HAPP может быть режим: System Proxy; TUN Mode. Если нужен полный VPN - включить TUN Mode в настройках.</p>
                                </div>
                            </div>
                        </div>

                        {/* AmneziaWG Instructions */}
                        <div className="bg-dark-900/50 rounded-lg p-3 border border-dark-700">
                            <h5 className="text-white text-xs font-bold mb-2 flex items-center gap-2">
                                <Shield className="w-3 h-3" />
                                AmneziaWG
                            </h5>
                            <div className="space-y-2 text-[11px] text-gray-300">
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        1
                                    </div>
                                    <p>
                                        Скачать приложение{' '}
                                        <a
                                            href="https://apps.apple.com/ru/app/amneziawg/id6478942365"
                                            target="_blank"
                                            className="text-blue-400 underline"
                                        >
                                            <b>AmneziaWG</b>
                                        </a>
                                        {' '}из App Store.
                                    </p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        2
                                    </div>
                                    <p>Установить приложение AmneziaWG.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        3
                                    </div>
                                    <p>Запустить приложение AmneziaWG.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        4
                                    </div>
                                    <p>Получить файл конфигурации AmneziaWG в боте и перенести его на компьютер.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        5
                                    </div>
                                    <p>В окне приложения нажать кнопку "+" или "Import".</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        6
                                    </div>
                                    <p>В открывшемся окне выбрать файл конфигурации AmneziaWG.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        7
                                    </div>
                                    <p>Подтвердить добавление VPN-конфигурации в macOS (если появится запрос).</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        8
                                    </div>
                                    <p>Сервер будет сохранён в списке подключений.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        9
                                    </div>
                                    <p>Нажать кнопку "Connect" / "Подключиться".</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        10
                                    </div>
                                    <p>Дождаться установки соединения.</p>
                                </div>
                                <div className="flex gap-2">
                                    <div className="min-w-[16px] h-4 bg-white text-[#0f172a] rounded-full flex items-center justify-center text-[9px] font-bold">
                                        11
                                    </div>
                                    <p>Подключение к VPN активировано.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/20 p-3 rounded-lg flex gap-3">
                <BookOpen className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                <p className="text-[10px] text-yellow-200">
                    Если у вас возникли проблемы с подключением, попробуйте
                    отключить VPN, затем подключить снова. Проблема может
                    возникать при переключении с мобильного интернета на WI-FI и
                    обратно.
                </p>
            </div>
        </div>
    );
}

export default App;
